import React, { useEffect, useState, useMemo } from 'react';
import { Card, Button, Col, Container, Form, Modal, Row, Spinner, Table, Badge, InputGroup, Nav, Tab, Pagination as BSPagination } from 'react-bootstrap';
import NavContainer from './Nav';
import OffCanvasNav from './OffCanvasNav';
import { dotoniwoofApi } from '../api/dotoniwooApi';
import { getCategoriesByType } from '../services/categoryApi';
import { getProducts as fetchProductsApi, getProductById, createProduct, updateProduct, deleteProduct } from '../services/productApi';
import { getItems as fetchItemsApi, createItem, updateItem, deleteItem } from '../services/itemApi';
import { useAuth } from '../context/AuthContext';
import { storage } from '../firebase/firebaseConfig';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { Helmet } from 'react-helmet';
import AlertModal from './AlertModal';
import useAlertModal from '../hooks/useAlertModal';
import BulkImportTab from './BulkImport/BulkImportTab';
import BulkEditTab from './BulkEdit/BulkEditTab';
import './ProductManagement.css';

const ProductForm = () => {
  const [product, setProduct] = useState({
    name: '',
    description: '',
    id_category: '',
    id_supplier: ''
  });

  const { user } = useAuth();
  const [dataResponse, setDataResponse] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [selectedCategoryType, setSelectedCategoryType] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');
  const [selectedWeight, setSelectedWeight] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [categoryTypes, setCategoryTypes] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [products, setProducts] = useState([]);
  const [colors, setColors] = useState([]);
  const [sizes, setSizes] = useState([]);
  const [weight, setWeight] = useState([]);
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const { showAlert, showAlertModal, alertMessage, alertType, hideAlert } = useAlertModal();

  const [weightData, setWeightData] = useState({ weight_name: '' });
  const [sizeData, setSizeData] = useState({ size_name: '' });
  const [colorData, setColorData] = useState({ color_name: '' });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [productPage, setProductPage] = useState(1);
  const PRODUCTS_PER_PAGE = 10;
  const [showEditProductModal, setShowEditProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [items, setItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [itemPage, setItemPage] = useState(1);
  const ITEMS_PER_PAGE = 10;
  const [showDeleteItemModal, setShowDeleteItemModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showEditItemModal, setShowEditItemModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editItemImage, setEditItemImage] = useState(null);
  const [editItemImagePreview, setEditItemImagePreview] = useState(null);
  const [showCopyItemModal, setShowCopyItemModal] = useState(false);
  const [copyingItem, setCopyingItem] = useState(null);
  const [copyItemImage, setCopyItemImage] = useState(null);
  const [copyItemImagePreview, setCopyItemImagePreview] = useState(null);

  /* ITEMS */
  const [itemData, setItemData] = useState({
    id_product: '',
    id_color: '',
    id_weight: '',
    id_size: '',
    stock: '',
    price: '',
    description: '',
    url: ''
  });

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Handlers
  const handleChange = (e) => {
    setProduct({
      ...product,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmitProduct = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const idCategoryRaw = selectedCategory || product.id_category;
      const idCategory = idCategoryRaw !== '' && idCategoryRaw != null
        ? parseInt(idCategoryRaw, 10)
        : NaN;
      if (!idCategoryRaw || Number.isNaN(idCategory)) {
        const msg = selectedCategoryType && categories.length === 0
          ? 'No hay categorías para este tipo. Cree categorías en la sección Categorías del menú.'
          : 'Seleccione una categoría.';
        showAlert(msg, 'error');
        setSubmitting(false);
        return;
      }
      const payload = {
        firebase_UID: user.uid,
        name: product.name.trim(),
        description: (product.description || '').trim(),
        id_category: idCategory
      };
      const result = await createProduct(payload);
      const data = result.data;
      setDataResponse(data);
      if (result.ok && (data?.code === 201 || data?.code === 200)) {
        showAlert(data?.message || 'Producto creado exitosamente', 'success');
        setProduct({ name: '', description: '', id_category: '', id_supplier: '' });
        setSelectedCategory('');
        setSelectedCategoryType('');
        setSelectedSupplier('');
        getProducts();
      } else {
        throw new Error(data?.message || 'Error al crear el producto');
      }
    } catch (error) {
      showAlert(error?.data?.message || error?.message || 'Error al realizar la operación', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitWeight = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await dotoniwoofApi.post("/weight.php",
        JSON.stringify({
          firebase_UID: user.uid,
          weight_name: weightData.weight_name,
        }));
      if (data.code === 201) {
        showAlert(data.message || 'Gramaje creado exitosamente', 'success');
        setWeightData({ weight_name: '' });
        getWeight();
      } else {
        throw new Error(data.message || 'Error al crear el gramaje');
      }
    } catch (error) {
      showAlert(error.response?.data?.message || error.message || 'Error al realizar la operación', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitSize = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await dotoniwoofApi.post("/size.php",
        JSON.stringify({
          firebase_UID: user.uid,
          size_name: sizeData.size_name
        }));
      if (data.code === 201) {
        showAlert(data.message || 'Talla creada exitosamente', 'success');
        setSizeData({ size_name: '' });
        getSize();
      } else {
        throw new Error(data.message || 'Error al crear la talla');
      }
    } catch (error) {
      showAlert(error.response?.data?.message || error.message || 'Error al realizar la operación', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitColor = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await dotoniwoofApi.post("/color.php",
        JSON.stringify({
          firebase_UID: user.uid,
          color_name: colorData.color_name
        }));
      if (data.code === 201) {
        showAlert(data.message || 'Color creado exitosamente', 'success');
        setColorData({ color_name: '' });
        getColor();
      } else {
        throw new Error(data.message || 'Error al crear el color');
      }
    } catch (error) {
      showAlert(error.response?.data?.message || error.message || 'Error al realizar la operación', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChangeWight = (e) => {
    const { name, value } = e.target;
    setWeightData({ [name]: value });
  };

  const handleChangeSize = (e) => {
    const { name, value } = e.target;
    setSizeData({ [name]: value });
  };

  const handleChangeColor = (e) => {
    const { name, value } = e.target;
    setColorData({ [name]: value });
  };

  const handleChangeItems = (e) => {
    const { name, value } = e.target;
    setItemData({ ...itemData, [name]: value });
  };

  const handleImageChange = (e) => {
    if (e.target.files[0]) {
      const file = e.target.files[0];
      setImage(file);
      // Crear preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImageToFirebase = (file) => {
    const ext = (file.name || '').split('.').pop() || 'jpg';
    const mime = file.type || 'image/jpeg';
    const newFileName = `image_${Date.now()}.${ext}`;
    const storageRef = ref(storage, `items/${newFileName}`);
    const uploadTask = uploadBytesResumable(storageRef, file, { contentType: mime });
    return new Promise((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        () => {},
        (error) => {
          console.error('Error uploading image:', error);
          reject(error);
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then(resolve).catch(reject);
        }
      );
    });
  };

  const handleImageUpload = async (callback) => {
    if (image) {
      try {
        const downloadURL = await uploadImageToFirebase(image);
        callback(downloadURL);
      } catch {
        showAlert('Error al subir la imagen', 'error');
        setSubmitting(false);
      }
    }
  };

  const handleEditItemImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setEditItemImage(file);
      setEditItemImagePreview(URL.createObjectURL(file));
    } else {
      setEditItemImage(null);
      setEditItemImagePreview(editingItem?.url || null);
    }
  };

  const handleSubmitItem = (e) => {
    e.preventDefault();
    if (!selectedProduct) {
      showAlert('Por favor seleccione un producto', 'error');
      return;
    }
    setSubmitting(true);
    handleImageUpload((imageUrl) => {
      sendDataToApi(imageUrl);
    });
  };

  const sendDataToApi = async (imageUrl) => {
    try {
      const payload = {
        firebase_UID: user.uid,
        id_product: selectedProduct.id_product,
        id_color: selectedColor || null,
        id_weight: selectedWeight || null,
        id_size: selectedSize || null,
        id_supplier: selectedSupplier || null,
        stock: itemData.stock,
        price: itemData.price,
        description: itemData.description,
        url: imageUrl
      };
      const result = await createItem(payload);
      const data = result.data;
      if (result.ok && (data?.code === 201 || data?.code === 200)) {
        showAlert(data?.message || 'Ítem creado exitosamente', 'success');
        setItemData({ id_product: '', id_color: '', id_weight: '', id_size: '', stock: '', price: '', description: '', url: '' });
        setImage(null);
        setImagePreview(null);
        setSelectedProduct(null);
        setSelectedColor('');
        setSelectedWeight('');
        setSelectedSize('');
        setSelectedSupplier('');
        getItems();
      } else {
        throw new Error(data?.message || 'Error al crear el ítem');
      }
    } catch (error) {
      showAlert(error?.data?.message || error?.message || 'Error al realizar la operación', 'error');
    } finally {
      setSubmitting(false);
    }
  };


  // Data fetching
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const res = await dotoniwoofApi.get(`/supplier.php`);
        if (res.data.code === 200) {
          setSuppliers(res.data.message);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    const getCategoryType = async () => {
      try {
        const res = await dotoniwoofApi.get(`/categoryType.php`);
        if (res.data.code === 200) {
          setCategoryTypes(res.data.message);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    getColor();
    getSize();
    getWeight();
    getProducts();
    getCategoryType();
    fetchSuppliers();
    getItems();
  }, []);

  const getColor = async () => {
    try {
      const res = await dotoniwoofApi.get(`/color.php`);
      if (res.data.code === 200) {
        setColors(res.data.message);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getSize = async () => {
    try {
      const res = await dotoniwoofApi.get(`/size.php`);
      if (res.data.code === 200) {
        setSizes(res.data.message);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getWeight = async () => {
    try {
      const res = await dotoniwoofApi.get(`/weight.php`);
      if (res.data.code === 200) {
        setWeight(res.data.message);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getProducts = async () => {
    try {
      const result = await fetchProductsApi();
      if (result.ok && result.data?.code === 200) {
        const raw = result.data.message;
        const productsData = Array.isArray(raw) ? raw : (raw?.products || []);
        setProducts(productsData);
      }
    } catch (err) {
      console.error('Error cargando productos:', err);
    } finally {
      setLoading(false);
    }
  };

  const getItems = async () => {
    setLoadingItems(true);
    try {
      const result = await fetchItemsApi();
      if (result.ok && result.data?.code === 200) {
        const raw = result.data.message;
        setItems(Array.isArray(raw) ? raw : (raw?.items || []));
      } else {
        setItems([]);
      }
    } catch (err) {
      console.error('Error cargando ítems:', err);
      setItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  const getCategoryByType = async (categoryType, keepSelection = false) => {
    if (!keepSelection) {
      setSelectedCategory('');
      setProduct((prev) => ({ ...prev, id_category: '' }));
    }
    setLoadingCategories(true);
    try {
      const result = await getCategoriesByType(categoryType);
      const raw = result.ok && result.data?.code === 200 ? result.data.message : null;
      const list = Array.isArray(raw) ? raw : (raw && Array.isArray(raw.categories) ? raw.categories : []);
      setCategories(list);
    } catch (err) {
      console.error('Error al cargar categorías por tipo:', err);
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleCategoryTypeChange = (e) => {
    const categoryTypeId = e.target.value;
    setSelectedCategoryType(categoryTypeId);
    setSelectedCategory('');
    if (categoryTypeId) {
      getCategoryByType(categoryTypeId);
    } else {
      setCategories([]);
    }
  };

  const handleCategoryChange = (e) => {
    const value = e.target.value;
    setSelectedCategory(value);
    setProduct((prev) => ({ ...prev, id_category: value }));
  };

  const handleSupplierChange = (e) => {
    setSelectedSupplier(e.target.value);
  };

  const handleColorChange = (e) => {
    setSelectedColor(e.target.value);
  };

  const handleSizeChange = (e) => {
    setSelectedSize(e.target.value);
  };

  const handleWeightChange = (e) => {
    setSelectedWeight(e.target.value);
  };

  const handleProductSelect = (e) => {
    const productId = e.target.value;
    const product = products.find(p => p.id_product === parseInt(productId));
    setSelectedProduct(product || null);
  };

  const filteredProducts = useMemo(() =>
    products.filter((p) =>
      (p.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.category_name || '').toLowerCase().includes(searchTerm.toLowerCase())
    ),
    [products, searchTerm]
  );

  const totalProductPages = Math.max(1, Math.ceil(filteredProducts.length / PRODUCTS_PER_PAGE));
  const paginatedProducts = useMemo(() => {
    const start = (productPage - 1) * PRODUCTS_PER_PAGE;
    return filteredProducts.slice(start, start + PRODUCTS_PER_PAGE);
  }, [filteredProducts, productPage]);

  const filteredProductsForSelect = useMemo(() =>
    products.filter((p) =>
      (p.name || '').toLowerCase().includes(productSearchTerm.toLowerCase())
    ),
    [products, productSearchTerm]
  );

  const filteredItems = useMemo(() => {
    const term = (itemSearchTerm || '').toLowerCase();
    if (!term) return items;
    return items.filter((item) => {
      const productName = item.product?.name || item.product_name || item.name || '';
      return (
        (item.description || item.sku || '').toLowerCase().includes(term) ||
        productName.toLowerCase().includes(term)
      );
    });
  }, [items, itemSearchTerm]);

  const totalItemPages = Math.max(1, Math.ceil(filteredItems.length / ITEMS_PER_PAGE));
  const paginatedItems = useMemo(() => {
    const start = (itemPage - 1) * ITEMS_PER_PAGE;
    return filteredItems.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredItems, itemPage]);

  const openEditProduct = (prod) => {
    setEditingProduct({ ...prod });
    setSelectedCategoryType(prod.id_category_type || '');
    setSelectedCategory(String(prod.id_category || ''));
    if (prod.id_category_type) getCategoryByType(prod.id_category_type, true);
    setShowEditProductModal(true);
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    if (!editingProduct?.id_product) return;
    setSubmitting(true);
    try {
      const idCategory = parseInt(selectedCategory || editingProduct.id_category, 10);
      if (!idCategory) {
        showAlert('Seleccione una categoría.', 'error');
        setSubmitting(false);
        return;
      }
      const result = await updateProduct(editingProduct.id_product, {
        firebase_UID: user.uid,
        name: (editingProduct.name || '').trim(),
        description: (editingProduct.description || '').trim(),
        id_category: idCategory
      });
      const data = result.data;
      if (result.ok && (data?.code === 200 || data?.code === 204)) {
        showAlert(data?.message || 'Producto actualizado', 'success');
        setShowEditProductModal(false);
        setEditingProduct(null);
        getProducts();
      } else {
        throw new Error(data?.message || 'Error al actualizar');
      }
    } catch (error) {
      showAlert(error?.data?.message || error?.message || 'Error al actualizar producto', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDeleteProduct = () => {
    if (!productToDelete?.id_product) return;
    setSubmitting(true);
    deleteProduct(productToDelete.id_product, user.uid)
      .then((result) => {
        const data = result.data;
        if (result.ok && (data?.code === 200 || data?.code === 204)) {
          showAlert(data?.message || 'Producto eliminado', 'success');
          setShowDeleteModal(false);
          setProductToDelete(null);
          getProducts();
        } else {
          showAlert(data?.message || 'No se pudo eliminar (puede tener ítems asociados)', 'error');
        }
      })
      .catch(() => showAlert('Error al eliminar producto', 'error'))
      .finally(() => setSubmitting(false));
  };

  const openEditItem = (item) => {
    setEditingItem({
      id_item: item.id_item,
      description: item.description || '',
      sku: item.sku || '',
      url: item.url || '',
      stock: item.stock ?? '',
      price: item.price ?? '',
      id_color: item.id_color || '',
      id_size: item.id_size || '',
      id_weight: item.id_weight || ''
    });
    setEditItemImage(null);
    setEditItemImagePreview(item.url || null);
    setShowEditItemModal(true);
  };

  const handleUpdateItem = async (e) => {
    e.preventDefault();
    if (!editingItem?.id_item) return;
    setSubmitting(true);
    try {
      let urlToSend = (editingItem.url || '').trim() || undefined;
      if (editItemImage) {
        try {
          urlToSend = await uploadImageToFirebase(editItemImage);
        } catch (err) {
          showAlert('Error al subir la imagen a Firebase', 'error');
          setSubmitting(false);
          return;
        }
      }
      const payload = {
        firebase_UID: user.uid,
        description: (editingItem.description || '').trim(),
        sku: (editingItem.sku || '').trim() || undefined,
        url: urlToSend,
        stock: editingItem.stock !== '' && editingItem.stock != null ? Number(editingItem.stock) : undefined,
        price: editingItem.price !== '' && editingItem.price != null ? Number(editingItem.price) : undefined,
        id_color: editingItem.id_color || undefined,
        id_size: editingItem.id_size || undefined,
        id_weight: editingItem.id_weight || undefined
      };
      const result = await updateItem(editingItem.id_item, payload);
      const data = result.data;
      if (result.ok && (data?.code === 200 || data?.code === 204)) {
        showAlert(data?.message || 'Ítem actualizado', 'success');
        setShowEditItemModal(false);
        setEditingItem(null);
        setEditItemImage(null);
        setEditItemImagePreview(null);
        getItems();
      } else {
        throw new Error(data?.message || 'Error al actualizar');
      }
    } catch (error) {
      showAlert(error?.data?.message || error?.message || 'Error al actualizar ítem', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDeleteItem = () => {
    if (!itemToDelete?.id_item) return;
    setSubmitting(true);
    deleteItem(itemToDelete.id_item, user.uid)
      .then((result) => {
        const data = result.data;
        if (result.ok && (data?.code === 200 || data?.code === 204)) {
          showAlert(data?.message || 'Ítem eliminado', 'success');
          setShowDeleteItemModal(false);
          setItemToDelete(null);
          getItems();
        } else {
          showAlert(data?.message || 'No se pudo eliminar (puede tener ventas asociadas)', 'error');
        }
      })
      .catch(() => showAlert('Error al eliminar ítem', 'error'))
      .finally(() => setSubmitting(false));
  };

  const openCopyItem = (item) => {
    console.log('openCopyItem - item recibido:', item);
    
    // Obtener id_product de diferentes posibles estructuras
    const productId = item.id_product || item.product?.id_product || item.idProduct;
    
    if (!productId) {
      console.error('No se encontró id_product en el item:', item);
      showAlert('Error: No se pudo obtener el ID del producto', 'error');
      return;
    }
    
    const newCopyingItem = {
      id_product: productId,
      description: `${item.description || ''} (Copia)`,
      sku: '',
      url: item.url || '',
      stock: 0,
      price: item.price ?? '',
      id_color: item.id_color || item.color?.id_color || '',
      id_size: item.id_size || item.size?.id_size || '',
      id_weight: item.id_weight || item.weight?.id_weight || '',
      id_supplier: item.id_supplier || item.supplier?.id_supplier || ''
    };
    
    console.log('openCopyItem - copyingItem configurado:', newCopyingItem);
    
    setCopyingItem(newCopyingItem);
    setCopyItemImage(null);
    setCopyItemImagePreview(item.url || null);
    setShowCopyItemModal(true);
  };

  const handleCopyItemImageChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setCopyItemImage(file);
      setCopyItemImagePreview(URL.createObjectURL(file));
    } else {
      setCopyItemImage(null);
      setCopyItemImagePreview(copyingItem?.url || null);
    }
  };

  const handleCopyItem = async (e) => {
    e.preventDefault();
    console.log('handleCopyItem iniciado', copyingItem);
    
    if (!copyingItem?.id_product) {
      showAlert('Error: No se encontró el producto', 'error');
      return;
    }
    
    setSubmitting(true);
    
    try {
      let urlToSend = (copyingItem.url || '').trim() || undefined;
      
      // Si hay una nueva imagen, subirla a Firebase
      if (copyItemImage) {
        console.log('Subiendo imagen a Firebase...');
        try {
          urlToSend = await uploadImageToFirebase(copyItemImage);
          console.log('Imagen subida exitosamente:', urlToSend);
        } catch (err) {
          console.error('Error al subir imagen:', err);
          showAlert('Error al subir la imagen a Firebase', 'error');
          setSubmitting(false);
          return;
        }
      }
      
      const payload = {
        firebase_UID: user.uid,
        id_product: copyingItem.id_product,
        description: (copyingItem.description || '').trim(),
        sku: (copyingItem.sku || '').trim() || undefined,
        url: urlToSend,
        stock: copyingItem.stock !== '' && copyingItem.stock != null ? Number(copyingItem.stock) : 0,
        price: copyingItem.price !== '' && copyingItem.price != null ? Number(copyingItem.price) : undefined,
        id_color: copyingItem.id_color || undefined,
        id_size: copyingItem.id_size || undefined,
        id_weight: copyingItem.id_weight || undefined,
        id_supplier: copyingItem.id_supplier || undefined
      };
      
      console.log('Payload a enviar:', payload);
      
      const result = await createItem(payload);
      console.log('Resultado del API:', result);
      
      const data = result.data;
      if (result.ok && (data?.code === 201 || data?.code === 200)) {
        showAlert(data?.message || 'Ítem copiado exitosamente', 'success');
        setShowCopyItemModal(false);
        setCopyingItem(null);
        setCopyItemImage(null);
        setCopyItemImagePreview(null);
        getItems();
      } else {
        console.error('Error en respuesta del API:', data);
        throw new Error(data?.message || 'Error al copiar el ítem');
      }
    } catch (error) {
      console.error('Error en handleCopyItem:', error);
      showAlert(error?.data?.message || error?.message || 'Error al copiar ítem', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Administración de Productos - DotoniWoof</title>
      </Helmet>
      <NavContainer />
      <OffCanvasNav />
      <Container fluid className="product-management-container">
        <div className="page-header">
          <h1 className="page-title">
            <i className="fas fa-box me-3"></i>
            Gestión de Productos
          </h1>
          <p className="page-subtitle">Crea y gestiona productos, items y atributos del catálogo</p>
        </div>

        <Tab.Container defaultActiveKey="products">
          <Row>
            <Col lg={3}>
              <Card className="modern-card sidebar-card">
                <Card.Body className="p-0">
                  <Nav variant="pills" className="flex-column modern-nav">
                    <Nav.Item>
                      <Nav.Link eventKey="products" className="modern-nav-link">
                        <i className="fas fa-box me-2"></i>
                        Productos
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="items" className="modern-nav-link">
                        <i className="fas fa-cube me-2"></i>
                        Items
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="attributes" className="modern-nav-link">
                        <i className="fas fa-tags me-2"></i>
                        Atributos
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="bulk-import" className="modern-nav-link">
                        <i className="fas fa-file-upload me-2"></i>
                        Importación Masiva
                      </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                      <Nav.Link eventKey="bulk-edit" className="modern-nav-link">
                        <i className="fas fa-edit me-2"></i>
                        Edición Masiva
                      </Nav.Link>
                    </Nav.Item>
                  </Nav>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={9}>
              <Tab.Content>
                {/* Tab: Productos */}
                <Tab.Pane eventKey="products">
                  <Card className="modern-card">
                    <Card.Header className="modern-card-header">
                      <h4 className="mb-0">
                        <i className="fas fa-box me-2"></i>
                        Crear Nuevo Producto
                      </h4>
                    </Card.Header>
                    <Card.Body>
                      <Form onSubmit={handleSubmitProduct}>
                        <Row>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label className="form-label-modern">
                                <i className="fas fa-tag me-2"></i>
                                Nombre del Producto <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Ej: Alimento Premium para Perros"
                                name="name"
                                value={product.name}
                                onChange={handleChange}
                                className="modern-input"
                                required
                              />
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label className="form-label-modern">
                                <i className="fas fa-align-left me-2"></i>
                                Descripción
                              </Form.Label>
                              <Form.Control
                                as="textarea"
                                rows={4}
                                placeholder="Describe las características del producto..."
                                name="description"
                                value={product.description}
                                onChange={handleChange}
                                className="modern-input"
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="form-label-modern">
                                <i className="fas fa-layer-group me-2"></i>
                                Tipo de Categoría <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Select
                                value={selectedCategoryType}
                                onChange={handleCategoryTypeChange}
                                className="modern-select"
                                required
                              >
                                <option value="">Seleccione un tipo...</option>
                                {categoryTypes.map((type) => (
                                  <option key={type.id_category_type} value={type.id_category_type}>
                                    {type.categoryTypeName}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            {selectedCategoryType && (
                              <Form.Group className="mb-3">
                                <Form.Label className="form-label-modern">
                                  <i className="fas fa-folder me-2"></i>
                                  Categoría <span className="text-danger">*</span>
                                </Form.Label>
                                {loadingCategories ? (
                                  <div className="d-flex align-items-center gap-2">
                                    <Spinner animation="border" size="sm" />
                                    <span className="text-muted small">Cargando categorías...</span>
                                  </div>
                                ) : categories.length === 0 ? (
                                  <div className="text-muted small py-2">
                                    No hay categorías para este tipo. Cree categorías en la sección Categorías.
                                  </div>
                                ) : (
                                  <Form.Select
                                    value={selectedCategory}
                                    onChange={handleCategoryChange}
                                    className="modern-select"
                                    required
                                  >
                                    <option value="">Seleccione una categoría...</option>
                                    {categories.map((cat) => {
                                      const catId = String(cat.id_category ?? cat.idCategory ?? '');
                                      const catName = cat.category_name ?? cat.categoryName ?? '';
                                      return (
                                        <option key={catId} value={catId}>
                                          {catName}
                                        </option>
                                      );
                                    })}
                                  </Form.Select>
                                )}
                              </Form.Group>
                            )}
                          </Col>
                        </Row>
                        <div className="form-actions">
                          <Button
                            type="submit"
                            variant="primary"
                            className="modern-btn-primary"
                            disabled={submitting}
                          >
                            {submitting ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Creando...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-plus me-2"></i>
                                Crear Producto
                              </>
                            )}
                          </Button>
                        </div>
                      </Form>
                    </Card.Body>
                  </Card>

                  {/* Lista de Productos */}
                  <Card className="modern-card mt-4">
                    <Card.Header className="modern-card-header">
                      <div className="d-flex justify-content-between align-items-center w-100 flex-wrap gap-2">
                        <h4 className="mb-0">
                          <i className="fas fa-list me-2"></i>
                          Lista de Productos
                        </h4>
                        <Badge bg="primary" className="fs-6">
                          {filteredProducts.length} producto{filteredProducts.length !== 1 ? 's' : ''}
                        </Badge>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <div className="product-search-wrap">
                        <div className="product-search-bar">
                          <i className="fas fa-search search-icon" />
                          <Form.Control
                            type="text"
                            placeholder="Buscar por nombre, descripción o categoría..."
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setProductPage(1); }}
                          />
                          {searchTerm && (
                            <button
                              type="button"
                              className="product-search-clear"
                              onClick={() => { setSearchTerm(''); setProductPage(1); }}
                            >
                              Limpiar
                            </button>
                          )}
                        </div>
                      </div>

                      {loading ? (
                        <div className="text-center py-5">
                          <Spinner animation="border" variant="primary" />
                        </div>
                      ) : filteredProducts.length === 0 ? (
                        <div className="product-empty-state">
                          <div className="empty-icon">📦</div>
                          <p className="text-muted mb-0">
                            {products.length === 0 ? 'No hay productos. Cree uno arriba.' : 'No hay resultados para la búsqueda.'}
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="table-responsive">
                            <Table hover className="modern-table product-table">
                              <thead>
                                <tr>
                                  <th>Nombre</th>
                                  <th>Descripción</th>
                                  <th>Categoría</th>
                                  <th className="text-end" style={{ width: '120px' }}>Acciones</th>
                                </tr>
                              </thead>
                              <tbody>
                                {paginatedProducts.map((prod) => (
                                  <tr key={prod.id_product}>
                                    <td className="fw-semibold">{prod.name}</td>
                                    <td>{(prod.description || '-').slice(0, 60)}{(prod.description || '').length > 60 ? '…' : ''}</td>
                                    <td>
                                      <Badge bg="secondary">{prod.category_name || '-'}</Badge>
                                    </td>
                                    <td className="text-end">
                                      <Button
                                        variant="link"
                                        className="btn-action btn-action-edit"
                                        title="Editar"
                                        onClick={() => openEditProduct(prod)}
                                      >
                                        <i className="fas fa-edit" />
                                      </Button>
                                      <Button
                                        variant="link"
                                        className="btn-action btn-action-delete"
                                        title="Eliminar"
                                        onClick={() => { setProductToDelete(prod); setShowDeleteModal(true); }}
                                      >
                                        <i className="fas fa-trash-alt" />
                                      </Button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                          {totalProductPages > 1 && (
                            <div className="product-pagination-wrap">
                              <small className="text-muted">
                                Página {productPage} de {totalProductPages} · {filteredProducts.length} resultado{filteredProducts.length !== 1 ? 's' : ''}
                              </small>
                              <BSPagination className="mb-0">
                                <BSPagination.First disabled={productPage === 1} onClick={() => setProductPage(1)} />
                                <BSPagination.Prev disabled={productPage === 1} onClick={() => setProductPage((p) => Math.max(1, p - 1))} />
                                {Array.from({ length: Math.min(5, totalProductPages) }, (_, i) => {
                                  let page = productPage - 2 + i;
                                  if (totalProductPages <= 5) page = i + 1;
                                  else if (page < 1) page = 1;
                                  else if (page > totalProductPages) page = totalProductPages;
                                  return (
                                    <BSPagination.Item key={page} active={page === productPage} onClick={() => setProductPage(page)}>
                                      {page}
                                    </BSPagination.Item>
                                  );
                                })}
                                <BSPagination.Next disabled={productPage === totalProductPages} onClick={() => setProductPage((p) => Math.min(totalProductPages, p + 1))} />
                                <BSPagination.Last disabled={productPage === totalProductPages} onClick={() => setProductPage(totalProductPages)} />
                              </BSPagination>
                            </div>
                          )}
                        </>
                      )}
                    </Card.Body>
                  </Card>
                </Tab.Pane>

                {/* Tab: Items */}
                <Tab.Pane eventKey="items">
                  <Card className="modern-card">
                    <Card.Header className="modern-card-header">
                      <h4 className="mb-0">
                        <i className="fas fa-cube me-2"></i>
                        Crear Nuevo Item
                      </h4>
                    </Card.Header>
                    <Card.Body>
                      <Form onSubmit={handleSubmitItem}>
                        <Row>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label className="form-label-modern">
                                <i className="fas fa-box me-2"></i>
                                Producto Padre <span className="text-danger">*</span>
                              </Form.Label>
                              <InputGroup>
                                <InputGroup.Text>
                                  <i className="fas fa-search"></i>
                                </InputGroup.Text>
                                <Form.Control
                                  type="text"
                                  placeholder="Buscar producto..."
                                  value={productSearchTerm}
                                  onChange={(e) => setProductSearchTerm(e.target.value)}
                                  className="modern-input"
                                />
                              </InputGroup>
                              <Form.Select
                                value={selectedProduct?.id_product || ''}
                                onChange={handleProductSelect}
                                className="modern-select mt-2"
                                required
                              >
                                <option value="">Seleccione un producto...</option>
                                {filteredProductsForSelect.map((prod) => (
                                  <option key={prod.id_product} value={prod.id_product}>
                                    {prod.name}
                                  </option>
                                ))}
                              </Form.Select>
                              {selectedProduct && (
                                <div className="selected-product-badge mt-2">
                                  <Badge bg="success">
                                    <i className="fas fa-check me-1"></i>
                                    {selectedProduct.name}
                                  </Badge>
                                </div>
                              )}
                            </Form.Group>
                          </Col>
                          <Col md={12}>
                            <Form.Group className="mb-3">
                              <Form.Label className="form-label-modern">
                                <i className="fas fa-align-left me-2"></i>
                                Descripción del Item <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                placeholder="Ej: Alimento Premium 15kg - Sabor Pollo"
                                name="description"
                                value={itemData.description}
                                onChange={handleChangeItems}
                                className="modern-input"
                                required
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="form-label-modern">
                                <i className="fas fa-warehouse me-2"></i>
                                Stock <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="number"
                                min="0"
                                name="stock"
                                value={itemData.stock}
                                onChange={handleChangeItems}
                                placeholder="0"
                                className="modern-input"
                                required
                              />
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="form-label-modern">
                                <i className="fas fa-dollar-sign me-2"></i>
                                Precio <span className="text-danger">*</span>
                              </Form.Label>
                              <InputGroup>
                                <InputGroup.Text>$</InputGroup.Text>
                                <Form.Control
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  name="price"
                                  value={itemData.price}
                                  onChange={handleChangeItems}
                                  placeholder="0.00"
                                  className="modern-input"
                                  required
                                />
                              </InputGroup>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label className="form-label-modern">
                                <i className="fas fa-palette me-2"></i>
                                Color
                              </Form.Label>
                              <Form.Select
                                value={selectedColor}
                                onChange={handleColorChange}
                                className="modern-select"
                              >
                                <option value="">Seleccione...</option>
                                {colors.map((color) => (
                                  <option key={color.id_color} value={color.id_color}>
                                    {color.color_name}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label className="form-label-modern">
                                <i className="fas fa-weight me-2"></i>
                                Gramaje
                              </Form.Label>
                              <Form.Select
                                value={selectedWeight}
                                onChange={handleWeightChange}
                                className="modern-select"
                              >
                                <option value="">Seleccione...</option>
                                {weight.map((w) => (
                                  <option key={w.id_weight} value={w.id_weight}>
                                    {w.weight_name}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={4}>
                            <Form.Group className="mb-3">
                              <Form.Label className="form-label-modern">
                                <i className="fas fa-ruler me-2"></i>
                                Talla
                              </Form.Label>
                              <Form.Select
                                value={selectedSize}
                                onChange={handleSizeChange}
                                className="modern-select"
                              >
                                <option value="">Seleccione...</option>
                                {sizes.map((size) => (
                                  <option key={size.id_size} value={size.id_size}>
                                    {size.size_name}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="form-label-modern">
                                <i className="fas fa-truck me-2"></i>
                                Proveedor
                              </Form.Label>
                              <Form.Select
                                value={selectedSupplier}
                                onChange={handleSupplierChange}
                                className="modern-select"
                              >
                                <option value="">Seleccione...</option>
                                {suppliers.map((supplier) => (
                                  <option key={supplier.id_supplier} value={supplier.id_supplier}>
                                    {supplier.name}
                                  </option>
                                ))}
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label className="form-label-modern">
                                <i className="fas fa-image me-2"></i>
                                Imagen del Item <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                className="modern-input"
                                required
                              />
                              {imagePreview && (
                                <div className="image-preview mt-3">
                                  <img
                                    src={imagePreview}
                                    alt="Preview"
                                    className="preview-image"
                                  />
                                </div>
                              )}
                            </Form.Group>
                          </Col>
                        </Row>
                        <div className="form-actions">
                          <Button
                            type="submit"
                            variant="primary"
                            className="modern-btn-primary"
                            disabled={submitting || !selectedProduct}
                          >
                            {submitting ? (
                              <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Creando...
                              </>
                            ) : (
                              <>
                                <i className="fas fa-plus me-2"></i>
                                Crear Item
                              </>
                            )}
                          </Button>
                        </div>
                      </Form>
                    </Card.Body>
                  </Card>

                  {/* Lista de Ítems */}
                  <Card className="modern-card mt-4">
                    <Card.Header className="modern-card-header">
                      <div className="d-flex justify-content-between align-items-center w-100 flex-wrap gap-2">
                        <h4 className="mb-0">
                          <i className="fas fa-cubes me-2"></i>
                          Lista de Ítems
                        </h4>
                        <Badge bg="primary" className="fs-6">
                          {filteredItems.length} ítem{filteredItems.length !== 1 ? 's' : ''}
                        </Badge>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <div className="product-search-wrap">
                        <div className="product-search-bar">
                          <i className="fas fa-search search-icon" />
                          <Form.Control
                            type="text"
                            placeholder="Buscar por SKU, descripción o producto..."
                            value={itemSearchTerm}
                            onChange={(e) => { setItemSearchTerm(e.target.value); setItemPage(1); }}
                          />
                          {itemSearchTerm && (
                            <button type="button" className="product-search-clear" onClick={() => { setItemSearchTerm(''); setItemPage(1); }}>
                              Limpiar
                            </button>
                          )}
                        </div>
                      </div>

                      {loadingItems ? (
                        <div className="text-center py-5">
                          <Spinner animation="border" variant="primary" />
                        </div>
                      ) : filteredItems.length === 0 ? (
                        <div className="product-empty-state">
                          <div className="empty-icon">📦</div>
                          <p className="text-muted mb-0">
                            {items.length === 0 ? 'No hay ítems. Cree uno arriba.' : 'No hay resultados para la búsqueda.'}
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="table-responsive">
                            <Table hover className="modern-table product-table">
                              <thead>
                                <tr>
                                  <th>SKU / ID</th>
                                  <th>Producto</th>
                                  <th>Descripción</th>
                                  <th>Precio</th>
                                  <th>Stock</th>
                                  <th className="text-end" style={{ width: '120px' }}>Acciones</th>
                                </tr>
                              </thead>
                              <tbody>
                                {paginatedItems.map((item) => {
                                  // Obtener nombre del producto desde diferentes posibles estructuras
                                  const productName = item.product?.name || item.product_name || item.name || '-';
                                  return (
                                    <tr key={item.id_item}>
                                      <td className="fw-semibold">{item.sku || item.id_item || '-'}</td>
                                      <td>{productName}</td>
                                      <td>{(item.description || '-').slice(0, 40)}{(item.description || '').length > 40 ? '…' : ''}</td>
                                      <td>{item.price != null ? `$ ${Number(item.price).toLocaleString('es-CO')}` : '-'}</td>
                                      <td>{item.stock != null ? item.stock : '-'}</td>
                                      <td className="text-end">
                                        <Button
                                          variant="link"
                                          className="btn-action btn-action-copy"
                                          title="Copiar ítem"
                                          onClick={() => openCopyItem(item)}
                                        >
                                          <i className="fas fa-copy" />
                                        </Button>
                                        <Button
                                          variant="link"
                                          className="btn-action btn-action-edit"
                                          title="Editar"
                                          onClick={() => openEditItem(item)}
                                        >
                                          <i className="fas fa-edit" />
                                        </Button>
                                        <Button
                                          variant="link"
                                          className="btn-action btn-action-delete"
                                          title="Eliminar"
                                          onClick={() => { setItemToDelete(item); setShowDeleteItemModal(true); }}
                                        >
                                          <i className="fas fa-trash-alt" />
                                        </Button>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </div>
                          {totalItemPages > 1 && (
                            <div className="product-pagination-wrap">
                              <small className="text-muted">
                                Página {itemPage} de {totalItemPages} · {filteredItems.length} resultado{filteredItems.length !== 1 ? 's' : ''}
                              </small>
                              <BSPagination className="mb-0">
                                <BSPagination.First disabled={itemPage === 1} onClick={() => setItemPage(1)} />
                                <BSPagination.Prev disabled={itemPage === 1} onClick={() => setItemPage((p) => Math.max(1, p - 1))} />
                                {Array.from({ length: Math.min(5, totalItemPages) }, (_, i) => {
                                  let page = itemPage - 2 + i;
                                  if (totalItemPages <= 5) page = i + 1;
                                  else if (page < 1) page = 1;
                                  else if (page > totalItemPages) page = totalItemPages;
                                  return (
                                    <BSPagination.Item key={page} active={page === itemPage} onClick={() => setItemPage(page)}>
                                      {page}
                                    </BSPagination.Item>
                                  );
                                })}
                                <BSPagination.Next disabled={itemPage === totalItemPages} onClick={() => setItemPage((p) => Math.min(totalItemPages, p + 1))} />
                                <BSPagination.Last disabled={itemPage === totalItemPages} onClick={() => setItemPage(totalItemPages)} />
                              </BSPagination>
                            </div>
                          )}
                        </>
                      )}
                    </Card.Body>
                  </Card>
                </Tab.Pane>

                {/* Tab: Atributos */}
                <Tab.Pane eventKey="attributes">
                  <Row>
                    <Col md={4}>
                      <Card className="modern-card">
                        <Card.Header className="modern-card-header">
                          <h5 className="mb-0">
                            <i className="fas fa-weight me-2"></i>
                            Gramajes
                          </h5>
                        </Card.Header>
                        <Card.Body>
                          <Form onSubmit={handleSubmitWeight}>
                            <Form.Group className="mb-3">
                              <Form.Label className="form-label-modern">
                                Nombre del Gramaje <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="weight_name"
                                value={weightData.weight_name}
                                onChange={handleChangeWight}
                                placeholder="Ej: 15kg"
                                className="modern-input"
                                required
                              />
                            </Form.Group>
                            <Button
                              type="submit"
                              variant="primary"
                              className="modern-btn-primary w-100"
                              disabled={submitting}
                            >
                              {submitting ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <>
                                  <i className="fas fa-plus me-2"></i>
                                  Crear Gramaje
                                </>
                              )}
                            </Button>
                          </Form>
                          
                          {/* Lista de Gramajes Creados */}
                          {weight.length > 0 && (
                            <div className="attributes-list mt-4">
                              <h6 className="attributes-list-title">
                                <i className="fas fa-list me-2"></i>
                                Gramajes Creados ({weight.length})
                              </h6>
                              <div className="attributes-list-items">
                                {weight.map((w) => (
                                  <Badge key={w.id_weight} bg="success" className="attribute-badge me-2 mb-2">
                                    <i className="fas fa-check-circle me-1"></i>
                                    {w.weight_name}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col md={4}>
                      <Card className="modern-card">
                        <Card.Header className="modern-card-header">
                          <h5 className="mb-0">
                            <i className="fas fa-ruler me-2"></i>
                            Tallas
                          </h5>
                        </Card.Header>
                        <Card.Body>
                          <Form onSubmit={handleSubmitSize}>
                            <Form.Group className="mb-3">
                              <Form.Label className="form-label-modern">
                                Nombre de la Talla <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="size_name"
                                value={sizeData.size_name}
                                onChange={handleChangeSize}
                                placeholder="Ej: Mediano"
                                className="modern-input"
                                required
                              />
                            </Form.Group>
                            <Button
                              type="submit"
                              variant="primary"
                              className="modern-btn-primary w-100"
                              disabled={submitting}
                            >
                              {submitting ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <>
                                  <i className="fas fa-plus me-2"></i>
                                  Crear Talla
                                </>
                              )}
                            </Button>
                          </Form>
                          
                          {/* Lista de Tallas Creadas */}
                          {sizes.length > 0 && (
                            <div className="attributes-list mt-4">
                              <h6 className="attributes-list-title">
                                <i className="fas fa-list me-2"></i>
                                Tallas Creadas ({sizes.length})
                              </h6>
                              <div className="attributes-list-items">
                                {sizes.map((s) => (
                                  <Badge key={s.id_size} bg="success" className="attribute-badge me-2 mb-2">
                                    <i className="fas fa-check-circle me-1"></i>
                                    {s.size_name}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>

                    <Col md={4}>
                      <Card className="modern-card">
                        <Card.Header className="modern-card-header">
                          <h5 className="mb-0">
                            <i className="fas fa-palette me-2"></i>
                            Colores
                          </h5>
                        </Card.Header>
                        <Card.Body>
                          <Form onSubmit={handleSubmitColor}>
                            <Form.Group className="mb-3">
                              <Form.Label className="form-label-modern">
                                Nombre del Color <span className="text-danger">*</span>
                              </Form.Label>
                              <Form.Control
                                type="text"
                                name="color_name"
                                value={colorData.color_name}
                                onChange={handleChangeColor}
                                placeholder="Ej: Rojo"
                                className="modern-input"
                                required
                              />
                            </Form.Group>
                            <Button
                              type="submit"
                              variant="primary"
                              className="modern-btn-primary w-100"
                              disabled={submitting}
                            >
                              {submitting ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <>
                                  <i className="fas fa-plus me-2"></i>
                                  Crear Color
                                </>
                              )}
                            </Button>
                          </Form>
                          
                          {/* Lista de Colores Creados */}
                          {colors.length > 0 && (
                            <div className="attributes-list mt-4">
                              <h6 className="attributes-list-title">
                                <i className="fas fa-list me-2"></i>
                                Colores Creados ({colors.length})
                              </h6>
                              <div className="attributes-list-items">
                                {colors.map((c) => (
                                  <Badge key={c.id_color} bg="success" className="attribute-badge me-2 mb-2">
                                    <i className="fas fa-check-circle me-1"></i>
                                    {c.color_name}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Tab.Pane>

                {/* Tab: Importación Masiva */}
                <Tab.Pane eventKey="bulk-import">
                  <BulkImportTab 
                    showAlert={showAlert}
                    onImportComplete={() => {
                      getProducts();
                      getItems();
                    }}
                  />
                </Tab.Pane>

                {/* Tab: Edición Masiva */}
                <Tab.Pane eventKey="bulk-edit">
                  <BulkEditTab 
                    showAlert={showAlert}
                    onEditComplete={() => {
                      getProducts();
                      getItems();
                    }}
                  />
                </Tab.Pane>
              </Tab.Content>
            </Col>
          </Row>
        </Tab.Container>
      </Container>

      {/* Modal Editar Producto */}
      <Modal show={showEditProductModal} onHide={() => { setShowEditProductModal(false); setEditingProduct(null); }} centered className="product-modal">
        <Modal.Header closeButton>
          <Modal.Title>Editar producto</Modal.Title>
        </Modal.Header>
        {editingProduct && (
          <Form onSubmit={handleUpdateProduct}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Nombre <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  value={editingProduct.name || ''}
                  onChange={(e) => setEditingProduct((p) => ({ ...p, name: e.target.value }))}
                  required
                  className="modern-input"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Descripción</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={editingProduct.description || ''}
                  onChange={(e) => setEditingProduct((p) => ({ ...p, description: e.target.value }))}
                  className="modern-input"
                />
              </Form.Group>
              {selectedCategoryType && (
                <Form.Group className="mb-3">
                  <Form.Label>Categoría <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="modern-select"
                    required
                  >
                    <option value="">Seleccione...</option>
                    {categories.map((cat) => {
                      const catId = String(cat.id_category ?? cat.idCategory ?? '');
                      const catName = cat.category_name ?? cat.categoryName ?? '';
                      return <option key={catId} value={catId}>{catName}</option>;
                    })}
                  </Form.Select>
                </Form.Group>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => { setShowEditProductModal(false); setEditingProduct(null); }}>
                Cancelar
              </Button>
              <Button type="submit" className="modern-btn-primary" disabled={submitting}>
                {submitting ? <Spinner animation="border" size="sm" /> : 'Guardar'}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>

      {/* Modal Confirmar eliminar producto */}
      <Modal show={showDeleteModal} onHide={() => { setShowDeleteModal(false); setProductToDelete(null); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Eliminar producto</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Eliminar el producto <strong>{productToDelete?.name}</strong>? Si tiene ítems asociados no se podrá eliminar.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowDeleteModal(false); setProductToDelete(null); }}>Cancelar</Button>
          <Button variant="danger" onClick={confirmDeleteProduct} disabled={submitting}>
            {submitting ? <Spinner animation="border" size="sm" /> : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Editar Ítem */}
      <Modal show={showEditItemModal} onHide={() => { setShowEditItemModal(false); setEditingItem(null); setEditItemImage(null); setEditItemImagePreview(null); }} centered className="product-modal">
        <Modal.Header closeButton>
          <Modal.Title>Editar ítem</Modal.Title>
        </Modal.Header>
        {editingItem && (
          <Form onSubmit={handleUpdateItem}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>SKU</Form.Label>
                <Form.Control
                  value={editingItem.sku || ''}
                  onChange={(e) => setEditingItem((p) => ({ ...p, sku: e.target.value }))}
                  placeholder="Código SKU"
                  className="modern-input"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Descripción <span className="text-danger">*</span></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={editingItem.description || ''}
                  onChange={(e) => setEditingItem((p) => ({ ...p, description: e.target.value }))}
                  required
                  className="modern-input"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Imagen del ítem</Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  onChange={handleEditItemImageChange}
                  className="modern-input"
                />
                <Form.Text className="text-muted">
                  Opcional. Si elige una imagen, se subirá a Firebase y se actualizará la URL en el backend.
                </Form.Text>
                {(editItemImagePreview || editingItem.url) && (
                  <div className="image-preview mt-2">
                    <img
                      src={editItemImagePreview || editingItem.url}
                      alt="Vista previa"
                      className="preview-image"
                      style={{ maxWidth: 120, maxHeight: 120, objectFit: 'cover', borderRadius: 8 }}
                    />
                  </div>
                )}
              </Form.Group>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Stock</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={editingItem.stock ?? ''}
                      onChange={(e) => setEditingItem((p) => ({ ...p, stock: e.target.value }))}
                      className="modern-input"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Precio</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingItem.price ?? ''}
                      onChange={(e) => setEditingItem((p) => ({ ...p, price: e.target.value }))}
                      className="modern-input"
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Color</Form.Label>
                    <Form.Select
                      value={editingItem.id_color || ''}
                      onChange={(e) => setEditingItem((p) => ({ ...p, id_color: e.target.value }))}
                      className="modern-select"
                    >
                      <option value="">—</option>
                      {colors.map((c) => (
                        <option key={c.id_color} value={c.id_color}>{c.color_name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Talla</Form.Label>
                    <Form.Select
                      value={editingItem.id_size || ''}
                      onChange={(e) => setEditingItem((p) => ({ ...p, id_size: e.target.value }))}
                      className="modern-select"
                    >
                      <option value="">—</option>
                      {sizes.map((s) => (
                        <option key={s.id_size} value={s.id_size}>{s.size_name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Gramaje</Form.Label>
                    <Form.Select
                      value={editingItem.id_weight || ''}
                      onChange={(e) => setEditingItem((p) => ({ ...p, id_weight: e.target.value }))}
                      className="modern-select"
                    >
                      <option value="">—</option>
                      {weight.map((w) => (
                        <option key={w.id_weight} value={w.id_weight}>{w.weight_name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => { setShowEditItemModal(false); setEditingItem(null); setEditItemImage(null); setEditItemImagePreview(null); }}>Cancelar</Button>
              <Button type="submit" className="modern-btn-primary" disabled={submitting}>
                {submitting ? <Spinner animation="border" size="sm" /> : 'Guardar'}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>

      {/* Modal Confirmar eliminar ítem */}
      <Modal show={showDeleteItemModal} onHide={() => { setShowDeleteItemModal(false); setItemToDelete(null); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>Eliminar ítem</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Eliminar el ítem <strong>{itemToDelete?.sku || itemToDelete?.id_item}</strong> ({itemToDelete?.description || itemToDelete?.product_name || ''})? Si tiene ventas asociadas no se podrá eliminar.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowDeleteItemModal(false); setItemToDelete(null); }}>Cancelar</Button>
          <Button variant="danger" onClick={confirmDeleteItem} disabled={submitting}>
            {submitting ? <Spinner animation="border" size="sm" /> : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Copiar Ítem */}
      <Modal 
        show={showCopyItemModal} 
        onHide={() => { 
          setShowCopyItemModal(false); 
          setCopyingItem(null); 
          setCopyItemImage(null); 
          setCopyItemImagePreview(null); 
        }} 
        centered 
        size="lg"
        className="product-modal copy-item-modal"
      >
        <Modal.Header closeButton className="bg-primary text-white">
          <Modal.Title>
            <i className="fas fa-copy me-2"></i>
            Copiar ítem
          </Modal.Title>
        </Modal.Header>
        {copyingItem && (
          <Form onSubmit={handleCopyItem}>
            <Modal.Body>
              <div className="alert alert-info d-flex align-items-center mb-4">
                <i className="fas fa-info-circle me-2 fs-5"></i>
                <div>
                  <strong>Duplicar ítem:</strong> Se creará un nuevo ítem con los datos del original. 
                  Puedes modificar cualquier campo antes de crear la copia.
                </div>
              </div>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="form-label-modern">
                      <i className="fas fa-align-left me-2"></i>
                      Descripción <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={copyingItem.description || ''}
                      onChange={(e) => setCopyingItem((p) => ({ ...p, description: e.target.value }))}
                      required
                      className="modern-input"
                      placeholder="Descripción del nuevo ítem"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="form-label-modern">
                      <i className="fas fa-barcode me-2"></i>
                      SKU
                    </Form.Label>
                    <Form.Control
                      value={copyingItem.sku || ''}
                      onChange={(e) => setCopyingItem((p) => ({ ...p, sku: e.target.value }))}
                      placeholder="Código SKU (opcional)"
                      className="modern-input"
                    />
                    <Form.Text className="text-muted">
                      Deja vacío para generar automáticamente
                    </Form.Text>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="form-label-modern">
                      <i className="fas fa-truck me-2"></i>
                      Proveedor
                    </Form.Label>
                    <Form.Select
                      value={copyingItem.id_supplier || ''}
                      onChange={(e) => setCopyingItem((p) => ({ ...p, id_supplier: e.target.value }))}
                      className="modern-select"
                    >
                      <option value="">Seleccione...</option>
                      {suppliers.map((supplier) => (
                        <option key={supplier.id_supplier} value={supplier.id_supplier}>
                          {supplier.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="form-label-modern">
                      <i className="fas fa-warehouse me-2"></i>
                      Stock <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={copyingItem.stock ?? ''}
                      onChange={(e) => setCopyingItem((p) => ({ ...p, stock: e.target.value }))}
                      className="modern-input"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="form-label-modern">
                      <i className="fas fa-dollar-sign me-2"></i>
                      Precio <span className="text-danger">*</span>
                    </Form.Label>
                    <InputGroup>
                      <InputGroup.Text>$</InputGroup.Text>
                      <Form.Control
                        type="number"
                        step="0.01"
                        min="0"
                        value={copyingItem.price ?? ''}
                        onChange={(e) => setCopyingItem((p) => ({ ...p, price: e.target.value }))}
                        className="modern-input"
                        required
                      />
                    </InputGroup>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="form-label-modern">
                      <i className="fas fa-palette me-2"></i>
                      Color
                    </Form.Label>
                    <Form.Select
                      value={copyingItem.id_color || ''}
                      onChange={(e) => setCopyingItem((p) => ({ ...p, id_color: e.target.value }))}
                      className="modern-select"
                    >
                      <option value="">—</option>
                      {colors.map((c) => (
                        <option key={c.id_color} value={c.id_color}>{c.color_name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="form-label-modern">
                      <i className="fas fa-ruler me-2"></i>
                      Talla
                    </Form.Label>
                    <Form.Select
                      value={copyingItem.id_size || ''}
                      onChange={(e) => setCopyingItem((p) => ({ ...p, id_size: e.target.value }))}
                      className="modern-select"
                    >
                      <option value="">—</option>
                      {sizes.map((s) => (
                        <option key={s.id_size} value={s.id_size}>{s.size_name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label className="form-label-modern">
                      <i className="fas fa-weight me-2"></i>
                      Gramaje
                    </Form.Label>
                    <Form.Select
                      value={copyingItem.id_weight || ''}
                      onChange={(e) => setCopyingItem((p) => ({ ...p, id_weight: e.target.value }))}
                      className="modern-select"
                    >
                      <option value="">—</option>
                      {weight.map((w) => (
                        <option key={w.id_weight} value={w.id_weight}>{w.weight_name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label className="form-label-modern">
                      <i className="fas fa-image me-2"></i>
                      Imagen del ítem
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept="image/*"
                      onChange={handleCopyItemImageChange}
                      className="modern-input"
                    />
                    <Form.Text className="text-muted">
                      Opcional. Si no seleccionas una nueva imagen, se usará la imagen del ítem original.
                    </Form.Text>
                    {(copyItemImagePreview || copyingItem.url) && (
                      <div className="image-preview mt-3 text-center">
                        <img
                          src={copyItemImagePreview || copyingItem.url}
                          alt="Vista previa"
                          className="preview-image"
                          style={{ 
                            maxWidth: 200, 
                            maxHeight: 200, 
                            objectFit: 'cover', 
                            borderRadius: 12,
                            boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                            border: '3px solid #e9ecef'
                          }}
                        />
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer className="bg-light">
              <Button 
                variant="secondary" 
                onClick={() => { 
                  setShowCopyItemModal(false); 
                  setCopyingItem(null); 
                  setCopyItemImage(null); 
                  setCopyItemImagePreview(null); 
                }}
              >
                <i className="fas fa-times me-2"></i>
                Cancelar
              </Button>
              <Button type="submit" className="modern-btn-primary" disabled={submitting}>
                {submitting ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Copiando...
                  </>
                ) : (
                  <>
                    <i className="fas fa-check me-2"></i>
                    Crear Copia
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        )}
      </Modal>

      {/* Modal de Alerta Temporal */}
      <AlertModal
        show={showAlertModal}
        onHide={hideAlert}
        message={alertMessage}
        type={alertType}
      />
    </>
  );
};

export default ProductForm;
